import {
  LatestQueueItem,
  OPT_JOBS_QUERY_KEY,
  OptJobStatus,
  useLatestQueueItem,
  usePublishLatestRegularWipOptJobMutation,
} from 'api/optJobs/optJobs.api'
import { useSystem } from 'api/systems/systems.api'
import { queryClient } from 'helpers/queryClient'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import ConfirmationDialog from './components/ConfirmDialog'
import PublishButton from './components/PublishButton'
import useTooltipTitle from './hooks/useTooltipTitle'
import { DialogContentData, Subtype, SubtypePublishInfo } from './PublishOptimizationResultButton.d'

const IS_PUBLISHING_STATUSES: Set<Partial<OptJobStatus>> = new Set(['Created', 'Running', 'Queued'])
const IS_OPTIMIZING_STATUSES: Set<Partial<OptJobStatus>> = new Set(['Created', 'Running', 'Queued'])

function hasNewerSuccessfulWip(
  latestSuccessfulRegularWIPQI: LatestQueueItem[],
  latestRegularQI: LatestQueueItem[],
  subtypes: Subtype[]
): boolean {
  // If there are subtypes, check each one
  if (subtypes.length > 0) {
    for (const st of subtypes) {
      const successfulWip = latestSuccessfulRegularWIPQI.find((qi) => qi.subtype === st.subtype)
      const regular = latestRegularQI.find((qi) => qi.subtype === st.subtype)
      // If there's a successful WIP that is newer than the regular (or no regular exists), we can publish
      if (successfulWip && (!regular || new Date(successfulWip.created_at) > new Date(regular.created_at))) {
        return true
      }
    }
    return false
  }

  // No subtypes - single comparison
  const successfulWip = latestSuccessfulRegularWIPQI.find((qi) => qi.subtype === null)
  const regular = latestRegularQI.find((qi) => qi.subtype === null)
  return !!successfulWip && (!regular || new Date(successfulWip.created_at) > new Date(regular.created_at))
}

function usePublishOptJobState(subtypes: Subtype[]) {
  const { activeSystem } = useAuth()
  const { data: latestRegularQI = [], status: latestRegularStatus } = useLatestQueueItem({
    type: 'regular',
    systemId: activeSystem?.id,
  })
  const { data: latestSuccessfulRegularQI = [], status: latestSuccessfulRegularStatus } = useLatestQueueItem({
    type: 'regular',
    queueItemStatus: 'Finished',
    systemId: activeSystem?.id,
  })
  const { data: latestRegularWIPQI = [], status: latestRegularWipStatus } = useLatestQueueItem({
    type: 'regular_wip',
    systemId: activeSystem?.id,
  })
  const { data: latestSuccessfulRegularWIPQI = [], status: latestSuccessfulRegularWipStatus } = useLatestQueueItem({
    type: 'regular_wip',
    queueItemStatus: 'Finished',
    systemId: activeSystem?.id,
  })

  const isFetchingJobs =
    latestRegularStatus === 'pending' ||
    latestSuccessfulRegularStatus === 'pending' ||
    latestRegularWipStatus === 'pending' ||
    latestSuccessfulRegularWipStatus === 'pending'

  const isPublishing = latestRegularQI.some((qi) => IS_PUBLISHING_STATUSES.has(qi.opt_job_status))
  const isOptimizing = latestRegularWIPQI.some((qi) => IS_OPTIMIZING_STATUSES.has(qi.opt_job_status))
  const hasPublishableWip = hasNewerSuccessfulWip(latestSuccessfulRegularWIPQI, latestRegularQI, subtypes)

  return {
    isPublishing,
    isOptimizing,
    isFetchingJobs,
    hasPublishableWip,
    latestRegularQI,
    latestSuccessfulRegularQI,
    latestRegularWIPQI,
    latestSuccessfulRegularWIPQI,
  }
}

function buildDialogContent(
  latestSuccessfulRegularQI: LatestQueueItem[],
  latestSuccessfulRegularWIPQI: LatestQueueItem[],
  isFetchingJobs: boolean,
  selectedSubtype: Subtype | null,
  systemSubtypes: Subtype[]
): DialogContentData {
  if (isFetchingJobs) {
    return {
      state: 'loading',
      subtypes: [],
      isMultipleSubtypes: false,
      allAlreadyPublished: false,
    }
  }

  const formatTimestamp = (timestamp: string | undefined): string | null => {
    if (!timestamp) return null
    return Datetime.toLocalTime(Datetime.toISOString(timestamp))
  }

  // Determine which subtypes to show
  const subtypesToProcess: Array<{ subtype: string | null; displayName: string | null }> = []

  if (selectedSubtype) {
    // Single subtype selected
    subtypesToProcess.push({
      subtype: selectedSubtype.subtype,
      displayName: selectedSubtype.subtype_display_name,
    })
  } else if (systemSubtypes.length > 0) {
    // Multiple subtypes - show all
    for (const st of systemSubtypes) {
      subtypesToProcess.push({
        subtype: st.subtype,
        displayName: st.subtype_display_name,
      })
    }
  } else {
    // No subtypes - single null entry
    subtypesToProcess.push({ subtype: null, displayName: null })
  }

  const subtypeInfos: SubtypePublishInfo[] = subtypesToProcess.map(({ subtype, displayName }) => {
    const wipItem = latestSuccessfulRegularWIPQI.find((qi) => qi.subtype === subtype)
    const regularItem = latestSuccessfulRegularQI.find((qi) => qi.subtype === subtype)

    const latestWipTimestamp = formatTimestamp(wipItem?.created_at)
    const latestPublishedTimestamp = formatTimestamp(regularItem?.created_at)

    const isInitialPublish = !!latestWipTimestamp && !latestPublishedTimestamp
    const alreadyPublished =
      latestWipTimestamp === latestPublishedTimestamp &&
      wipItem?.opt_job_status === 'Finished' &&
      regularItem?.opt_job_status === 'Finished'

    return {
      subtype,
      subtypeDisplayName: displayName,
      latestWipTimestamp,
      latestPublishedTimestamp,
      isInitialPublish,
      alreadyPublished,
    }
  })

  const hasAnyWipJob = subtypeInfos.some((info) => info.latestWipTimestamp)
  const allAlreadyPublished = subtypeInfos.length > 0 && subtypeInfos.every((info) => info.alreadyPublished)

  if (!hasAnyWipJob) {
    return {
      state: 'empty',
      subtypes: subtypeInfos,
      isMultipleSubtypes: subtypesToProcess.length > 1,
      allAlreadyPublished: false,
    }
  }

  const allInitial = subtypeInfos.every((info) => info.isInitialPublish || !info.latestWipTimestamp)
  const state = allInitial ? 'initial' : 'ok_to_publish'

  return {
    state,
    subtypes: subtypeInfos,
    isMultipleSubtypes: subtypesToProcess.length > 1,
    allAlreadyPublished,
  }
}

export default function PublishOptimizationResultButton(): ReactElement | null {
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [selectedSubtype, setSelectedSubtype] = useState<Subtype | null>(null)
  const [isOptimisticallyPublishing, setIsOptimisticallyPublishing] = useState(false)
  const wasPublishingRef = useRef(false)
  const { activeSystem } = useAuth()
  const { data: system } = useSystem({ id: activeSystem?.id })
  const { mutate: publishMutation } = usePublishLatestRegularWipOptJobMutation()

  const allRegularWipSubtypes = system?.subtypes?.regular_wip || []
  const isPublishManuallyDisabled =
    allRegularWipSubtypes.length > 0 && allRegularWipSubtypes.every((s) => s.enable_manually_trigger_opt_job === false)
  const subtypes = allRegularWipSubtypes.filter((subtype) => subtype.enable_manually_trigger_opt_job !== false)
  const hasSubtypes = !!subtypes.length

  const {
    isPublishing: isPublishingFromApi,
    isOptimizing,
    isFetchingJobs,
    hasPublishableWip,
    latestRegularQI,
    latestSuccessfulRegularQI,
    latestRegularWIPQI,
    latestSuccessfulRegularWIPQI,
  } = usePublishOptJobState(subtypes)

  // Combine API publishing state with optimistic state for immediate UI feedback
  const isPublishing = isPublishingFromApi || isOptimisticallyPublishing

  // Reset optimistic state once API confirms publishing started
  useEffect(() => {
    if (isPublishingFromApi && isOptimisticallyPublishing) {
      setIsOptimisticallyPublishing(false)
    }
  }, [isPublishingFromApi, isOptimisticallyPublishing])

  // Close modal automatically when publishing finishes successfully
  // This handles two cases:
  // 1. Normal flow: wasPublishingRef was true (API showed publishing state), now finished
  // 2. Super-fast backend: optimistically publishing, API never showed intermediate state,
  //    but now hasPublishableWip is false (meaning publish completed successfully)
  // 3. Optimistic publishing complete: timestamps match between regular and successful WIP
  useEffect(() => {
    const publishingFinishedNormally = wasPublishingRef.current && !isPublishing
    const superFastPublishComplete = isOptimisticallyPublishing && !isPublishingFromApi && !hasPublishableWip

    // Check if optimistic publishing is complete by comparing timestamps
    const isOptimisticPublishComplete = (): boolean => {
      if (!isOptimisticallyPublishing) return false

      if (selectedSubtype) {
        // Check only the selected subtype
        const regularItem = latestRegularQI.find((qi) => qi.subtype === selectedSubtype.subtype)
        const successfulWipItem = latestSuccessfulRegularWIPQI.find((qi) => qi.subtype === selectedSubtype.subtype)
        return !!(
          regularItem?.created_at &&
          successfulWipItem?.created_at &&
          regularItem.created_at === successfulWipItem.created_at &&
          regularItem.queue_item_status === 'Finished'
        )
      }

      // Check all subtypes (or null subtype if no subtypes exist)
      const subtypesToCheck = subtypes.length > 0 ? subtypes.map((st) => st.subtype) : [null]
      return subtypesToCheck.every((subtype) => {
        const regularItem = latestRegularQI.find((qi) => qi.subtype === subtype)
        const successfulWipItem = latestSuccessfulRegularWIPQI.find((qi) => qi.subtype === subtype)
        return (
          regularItem?.created_at &&
          successfulWipItem?.created_at &&
          regularItem.created_at === successfulWipItem.created_at &&
          regularItem.queue_item_status === 'Finished'
        )
      })
    }

    if (
      (publishingFinishedNormally || superFastPublishComplete || isOptimisticPublishComplete()) &&
      openConfirmationModal
    ) {
      handleCloseModal()
    }
    wasPublishingRef.current = isPublishing
  }, [
    isPublishing,
    isPublishingFromApi,
    isOptimisticallyPublishing,
    hasPublishableWip,
    openConfirmationModal,
    selectedSubtype,
    latestRegularQI,
    latestSuccessfulRegularWIPQI,
    subtypes,
  ])

  const isButtonDisabled =
    isPublishing || isOptimizing || isFetchingJobs || !hasPublishableWip || isPublishManuallyDisabled

  const tooltipTitle = useTooltipTitle({
    hasOptJob: !!latestRegularWIPQI.length,
    hasPublishableWip,
    isPublishing,
    isOptimizing,
  })

  const dialogContent = useMemo(
    () =>
      buildDialogContent(
        latestSuccessfulRegularQI,
        latestSuccessfulRegularWIPQI,
        isFetchingJobs,
        selectedSubtype,
        subtypes
      ),
    [latestSuccessfulRegularQI, latestSuccessfulRegularWIPQI, isFetchingJobs, selectedSubtype, subtypes]
  )

  function handleOpenModal(subtype?: Subtype) {
    setSelectedSubtype(subtype ?? null)
    setOpenConfirmationModal(true)
  }

  function handleCloseModal() {
    setOpenConfirmationModal(false)
    setSelectedSubtype(null)
    setIsOptimisticallyPublishing(false)
  }

  function handleSubmit() {
    // If a specific subtype is selected, pass it. Otherwise, call with no subtype to publish all.
    // Modal will be closed automatically by the useEffect when publishing finishes
    if (!isPublishingFromApi) {
      setIsOptimisticallyPublishing(true)
    }

    if (selectedSubtype) {
      publishMutation({ subtype: selectedSubtype.subtype })
    } else {
      publishMutation({})
    }
  }

  return (
    <>
      <PublishButton
        hasSubtypes={hasSubtypes}
        subtypes={subtypes}
        tooltipTitle={tooltipTitle}
        isPublishing={isPublishing}
        isButtonDisabled={isButtonDisabled}
        onOpenModal={handleOpenModal}
      />

      <ConfirmationDialog
        isOpen={openConfirmationModal}
        onClose={() => {
          handleCloseModal()
          queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, `latestQueueItem`, 'regular'] })
        }}
        onSubmit={handleSubmit}
        isPublishing={isPublishing}
        dialogContent={dialogContent}
        selectedSubtype={selectedSubtype}
      />
    </>
  )
}
